import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, Clock, ArrowRight } from "lucide-react";
import SectionContainer from "../components/SectionContainer";

const QUEUE_ITEMS = [
  { id: 1, action: "Update Salesforce opportunity to Technical Eval", tool: "Salesforce" },
  { id: 2, action: "Draft personalized follow-up email to Sarah K.",  tool: "Gmail"      },
  { id: 3, action: "Create onboarding scope ticket",                  tool: "Jira"       },
  { id: 4, action: "Post meeting summary to #revenue-team",           tool: "Slack"      },
  { id: 5, action: "File meeting notes and next steps",               tool: "Notion"     },
  { id: 6, action: "Schedule Q3 pilot kickoff meeting",               tool: "Calendar"   },
];

// Tool accent colors — subtle, dark-theme appropriate
const TOOL_COLORS = {
  Salesforce: "#00A1E0",
  Gmail:      "#EA4335",
  Jira:       "#0052CC",
  Slack:      "#4A154B",
  Notion:     "#ffffff",
  Calendar:   "#4285F4",
};

function QueueItem({ item, status }) {
  const isCompleted = status === "completed";
  const isRunning   = status === "running";
  const isPending   = status === "pending";

  return (
    <motion.div
      layout
      className={`
        relative flex items-center gap-3 rounded-lg px-4 py-3 transition-all duration-500
        ${isRunning   ? "bg-[#0092B4]/[0.12] border border-[#0092B4]/25" : ""}
        ${isCompleted ? "bg-white/[0.03] border border-white/[0.05]"     : ""}
        ${isPending   ? "bg-white/[0.02] border border-white/[0.04]"     : ""}
      `}
    >
      {/* Running glow line */}
      {isRunning && (
        <div className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-lg bg-[#0092B4]" />
      )}

      {/* Status icon */}
      <div className="shrink-0 w-5 flex justify-center">
        {isCompleted && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 400, damping: 18 }}
          >
            <CheckCircle2 className="h-4 w-4 text-emerald-400" strokeWidth={2} />
          </motion.div>
        )}
        {isRunning && <Loader2 className="h-4 w-4 text-[#0092B4] animate-spin" strokeWidth={2} />}
        {isPending && <Clock   className="h-4 w-4 text-white/35"  strokeWidth={2} />}
      </div>

      {/* Action text */}
      <span className={`flex-1 text-[13px] leading-snug transition-all duration-400 ${
        isCompleted ? "text-white/50" :
        isRunning   ? "text-white/90 font-medium" :
                      "text-white/55"
      }`}>
        {item.action}
      </span>

      {/* Tool badge */}
      <span
        className="shrink-0 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded"
        style={{
          color:           isCompleted || isPending ? "rgba(255,255,255,0.35)" : TOOL_COLORS[item.tool],
          backgroundColor: isRunning ? `${TOOL_COLORS[item.tool]}18` : "transparent",
        }}
      >
        {item.tool}
      </span>
    </motion.div>
  );
}

function LiveQueue() {
  const [statuses, setStatuses] = useState(Array(QUEUE_ITEMS.length).fill("pending"));

  useEffect(() => {
    const timers = [];
    QUEUE_ITEMS.forEach((_, i) => {
      timers.push(setTimeout(() => {
        setStatuses(prev => { const n = [...prev]; n[i] = "running"; return n; });
      }, 500 + i * 900));
      timers.push(setTimeout(() => {
        setStatuses(prev => { const n = [...prev]; n[i] = "completed"; return n; });
      }, 1400 + i * 900));
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="space-y-1.5">
      {QUEUE_ITEMS.map((item, i) => (
        <QueueItem key={item.id} item={item} status={statuses[i]} index={i} />
      ))}
    </div>
  );
}

export default function ExecutionSection() {
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setCycle(c => c + 1), 10000);
    return () => clearTimeout(t);
  }, [cycle]);

  return (
    <SectionContainer id="execution" className="py-20 sm:py-28">
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-2 lg:gap-20 items-center">

        {/* ── Copy ── */}
        <div>
          <h2 className="text-[clamp(1.8rem,4vw,2.85rem)] font-bold tracking-tight text-neutral-900 mb-5">
            Shadow doesn't stop at the summary.
          </h2>
          <p className="text-[15px] text-neutral-500 leading-relaxed mb-8">
            Most tools summarize your meetings. Shadow completes the work. Every action item detected on
            a call becomes a task in motion — drafted, dispatched, and tracked across the tools your
            team already uses.
          </p>

          <div className="space-y-5">
            {[
              {
                title: "CRM updated without lifting a finger",
                body:  "Opportunity stage, next steps, close date, and activity logs written directly to Salesforce after every call.",
              },
              {
                title: "Follow-ups drafted in your voice",
                body:  "Personalized recap emails written in your style, with full call context — ready to send before the day ends.",
              },
              {
                title: "Action items in Jira or Linear — with context",
                body:  "Every task lands assigned, with the call context attached, so whoever picks it up knows exactly what to do.",
              },
            ].map(item => (
              <div key={item.title} className="flex gap-4">
                <div className="mt-0.5 h-6 w-6 rounded-full bg-[#0092B4]/10 flex items-center justify-center shrink-0">
                  <ArrowRight className="h-3.5 w-3.5 text-[#0092B4]" strokeWidth={2.5} />
                </div>
                <div>
                  <p className="text-[14px] font-semibold text-neutral-900">{item.title}</p>
                  <p className="mt-0.5 text-[13px] text-neutral-500">{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Dark execution queue panel ── */}
        <div>
          <div
            className="rounded-2xl overflow-hidden border border-white/[0.08]"
            style={{
              background: "linear-gradient(145deg, #0D1525 0%, #070D1A 100%)",
              boxShadow: "0 24px 60px -12px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)",
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
              <div className="flex items-center gap-3">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-white/35">
                  Execution Queue
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                </span>
                <span className="text-[11px] font-medium text-emerald-400">Running</span>
              </div>
            </div>

            {/* Queue items */}
            <div className="p-3">
              <LiveQueue key={cycle} />
            </div>
          </div>
        </div>

      </div>
    </SectionContainer>
  );
}
